import config from '../../../config';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@coolblue-development/freddy';
import { runChatAttachmentHarness } from './chatAttachmentHarness';

type ChatDetails = {
  contactId: string;
  participantId: string;
  participantToken: string;
};

type ChatApiResponse = {
  data: {
    startChatResult: ChatDetails;
    sessionType: string;
    region: string;
  };
};

type BearerToken = {
  access_token: string;
};

interface GenerateChatButtonProps {
  withAttachments?: boolean;
}

const GenerateChatButton = ({ withAttachments = false }: GenerateChatButtonProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const { chatApiAuthUrl, chatApiAuthUsername, chatApiAuthPassword, chatApiUrl } = config;

  const getToken = async (): Promise<BearerToken> => {
    const credentials = btoa(`${chatApiAuthUsername}:${chatApiAuthPassword}`);

    const response = await fetch(chatApiAuthUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${credentials}`
      },
      body: new URLSearchParams({ grant_type: 'client_credentials' }).toString()
    });

    const data = (await response.json()) as BearerToken;
    if (!response.ok) throw new Error('Failed to fetch token');

    return data;
  };

  const sendChatRequest = async (bearerToken: BearerToken): Promise<ChatDetails> => {
    setLoading(true);
    const chatApiUrlWithId = `${chatApiUrl ?? ''}${uuidv4()}`;
    const DisplayName = 'some-name';
    const Locale = 'nl-NL';
    const TouchPoint = 'cato-ui';
    const SubsidiaryId = '1';
    const TouchpointLocation = 'cato';
    const RequestedAt = new Date().toISOString();
    const ChatBotSummary = 'Chat initiated via Cato UI';
    const EstimatedQueueWaitingTime = (Math.random() * 60).toString();

    const response = await fetch(chatApiUrlWithId, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken.access_token}`
      },
      body: JSON.stringify({
        displayName: DisplayName,
        locale: Locale,
        touchPoint: TouchPoint,
        intentInteractionId: uuidv4(),
        subsidiaryId: SubsidiaryId,
        touchpointLocation: TouchpointLocation,
        requestedAt: RequestedAt,
        chatBotSummary: ChatBotSummary,
        estimatedQueueWaitingTime: EstimatedQueueWaitingTime
      })
    });

    const data = (await response.json()) as ChatApiResponse;
    if (!response.ok) throw new Error('Failed to fetch chats');

    const { contactId, participantId, participantToken } = data.data.startChatResult;
    return { contactId, participantId, participantToken };
  };

  const connectChatSession = async (
    chatDetails: ChatDetails
  ): Promise<ReturnType<typeof connect.ChatSession.create>> => {
    const region = 'eu-central-1';
    connect.ChatSession.setGlobalConfig({ region: region });

    const customerChatSession = connect.ChatSession.create({
      chatDetails: {
        contactId: chatDetails.contactId,
        participantId: chatDetails.participantId,
        participantToken: chatDetails.participantToken
      },
      type: 'CUSTOMER'
    });

    await customerChatSession.connect();
    return customerChatSession;
  };
  const createChat = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = await getToken();
      const requestData = await sendChatRequest(token);
      const session = await connectChatSession(requestData);
      if (withAttachments) {
        void runChatAttachmentHarness(session, requestData.contactId, token.access_token).catch(err =>
          console.error(`Chat attachment harness failed for ${requestData.contactId}:`, err)
        );
      }
    } catch (err) {
      console.error('Failed to connect chat session:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button variant="primary" onClick={createChat} loading={loading}>
      {withAttachments ? 'Generate Chat + Attachments 📎' : 'Generate Chat 💬'}
    </Button>
  );
};

export default GenerateChatButton;
